import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {useStores} from "~/stores";
import {notificationStore} from "~/stores/init";
import {
    databagUrl,
    taxonomyDetailUrl,
    taxonomyListUrl,
} from "~/pages/taxonomy-builder-v2/services/taxonomyBuilderUrls";

export type TaxonomySettings = {
    name: string;
    /** The taxonomy's depth: how many levels every category needs before it can be sent for approval. */
    size: number;
    /** The taxonomy the databag uses. */
    linkedTaxonomyId: number;
};

type Variables = {
    taxonomy: m_taxonomy.FullSerializer;
    databagId: number;
    settings: TaxonomySettings;
};

/**
 * Saves the Mithra staff settings, as the current builder's staff fields do: `PATCH taxonomy/{id}/` for the
 * name and depth, and `PATCH bag/{id}/` to link the databag to another taxonomy (then the databag is
 * reloaded, so the builder shows that taxonomy). Only what changed is sent.
 */
export function useSaveTaxonomySettingsMutation() {
    const queryClient = useQueryClient();
    const {bagLoadingStore} = useStores();

    return useMutation<void, DefaultError, Variables>({
        mutationFn: async ({taxonomy, databagId, settings}) => {
            const update: Partial<m_taxonomy.UpdateSerializer> = {
                ...(settings.name !== taxonomy.name && {name: settings.name}),
                ...(settings.size !== taxonomy.size && {size: settings.size}),
            };
            if (Object.keys(update).length > 0) {
                const {data} = await queryHttp.patch<m_taxonomy.UpdateSerializer>(taxonomyDetailUrl(taxonomy.id), update);
                queryClient.setQueryData<m_taxonomy.FullSerializer>(
                    [taxonomyDetailUrl(taxonomy.id)],
                    cached => cached && {...cached, ...data},
                );
                void queryClient.invalidateQueries({queryKey: [taxonomyListUrl()]});
            }
            if (settings.linkedTaxonomyId !== taxonomy.id) {
                await queryHttp.patch(databagUrl(databagId), {taxonomy: settings.linkedTaxonomyId});
                await bagLoadingStore.loadBag(databagId);
            }
        },
        onSuccess: () => notificationStore.show('Taxonomy settings saved.', {
            severity: 'success',
            id: 'taxonomy-settings-saved',
            autoHideDuration: 3000,
        }),
        onError: () => notificationStore.show('Cannot save the taxonomy settings. Please try again.', {
            severity: 'error',
            id: 'taxonomy-settings-error',
            autoHideDuration: 6000,
        }),
    });
}
